import React, { useEffect, useState } from "react";
import { Card, Grid, Pagination, CircularProgress, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import { Message, Star, BugReport, PendingActions } from "@mui/icons-material";
import { useGetAllFeedbacksQuery } from "lib/store/service/feedback";
import { getTableData } from "./data/tableData";

function FeedbacksManagement() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [platform, setPlatform] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");

  const [tableData, setTableData] = useState({ columns: [], rows: [] });

  const { data, isLoading, isFetching, refetch } = useGetAllFeedbacksQuery({
    page,
    limit,
    platform,
    category,
    status,
  });

  const totalPages = data?.response?.data?.totalPages || data?.data?.totalPages || 1;
  const stats = data?.response?.data?.stats || data?.data?.stats || {
    totalCount: 0,
    pendingCount: 0,
    bugCount: 0,
    avgRating: 5.0,
  };

  useEffect(() => {
    setTableData(getTableData(data || {}, refetch));
  }, [data, refetch]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Analytics Header Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <VuiBox>
                <VuiTypography variant="caption" color="text">TOTAL FEEDBACKS</VuiTypography>
                <VuiTypography variant="h4" color="white" fontWeight="bold">
                  💬 {stats.totalCount}
                </VuiTypography>
              </VuiBox>
              <VuiBox p={1.5} borderRadius="12px" sx={{ backgroundColor: "rgba(6, 182, 212, 0.15)", color: "#06b6d4" }}>
                <Message />
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <VuiBox>
                <VuiTypography variant="caption" color="text">PENDING REVIEWS</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  ⏳ {stats.pendingCount}
                </VuiTypography>
              </VuiBox>
              <VuiBox p={1.5} borderRadius="12px" sx={{ backgroundColor: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" }}>
                <PendingActions />
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <VuiBox>
                <VuiTypography variant="caption" color="text">BUG REPORTS</VuiTypography>
                <VuiTypography variant="h4" color="error" fontWeight="bold">
                  🐛 {stats.bugCount}
                </VuiTypography>
              </VuiBox>
              <VuiBox p={1.5} borderRadius="12px" sx={{ backgroundColor: "rgba(244, 63, 94, 0.15)", color: "#f43f5e" }}>
                <BugReport />
              </VuiBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <VuiBox>
                <VuiTypography variant="caption" color="text">AVERAGE RATING</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  ⭐ {stats.avgRating} / 5
                </VuiTypography>
              </VuiBox>
              <VuiBox p={1.5} borderRadius="12px" sx={{ backgroundColor: "rgba(234, 179, 8, 0.15)", color: "#eab308" }}>
                <Star />
              </VuiBox>
            </Card>
          </Grid>
        </Grid>

        {/* Main Feedbacks Table */}
        <VuiBox mb={3}>
          <Card>
            <VuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} p={3} gap={2}>
              <VuiBox>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  User Feedbacks & Suggestions
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Sorted latest top first. Review bug reports, UI feedback, and improvement ideas.
                </VuiTypography>
              </VuiBox>

              {/* Filters */}
              <VuiBox display="flex" gap={1.5} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={platform}
                    onChange={(e) => { setPlatform(e.target.value); setPage(1); }}
                    displayEmpty
                    sx={{ color: "#fff", backgroundColor: "#1e293b", borderRadius: "8px", "& .MuiSvgIcon-root": { color: "#fff" } }}
                  >
                    <MenuItem value="all">All Platforms</MenuItem>
                    <MenuItem value="playground">Playground</MenuItem>
                    <MenuItem value="creator">Creator</MenuItem>
                    <MenuItem value="web">Web</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                    displayEmpty
                    sx={{ color: "#fff", backgroundColor: "#1e293b", borderRadius: "8px", "& .MuiSvgIcon-root": { color: "#fff" } }}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    <MenuItem value="Bug Report">Bug Report</MenuItem>
                    <MenuItem value="Feature Request">Feature Request</MenuItem>
                    <MenuItem value="UI/UX Feedback">UI/UX Feedback</MenuItem>
                    <MenuItem value="General Improvement">General Improvement</MenuItem>
                  </Select>
                </FormControl>
              </VuiBox>
            </VuiBox>

            <VuiBox p={2} sx={{ minHeight: "50vh" }}>
              {isLoading || isFetching ? (
                <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                  <CircularProgress color="info" size={40} />
                  <VuiTypography variant="button" color="text" fontWeight="bold">
                    Loading Latest Feedbacks...
                  </VuiTypography>
                </VuiBox>
              ) : tableData.rows?.length === 0 ? (
                <VuiBox display="flex" justifyContent="center" alignItems="center" py={8}>
                  <VuiTypography variant="button" color="text">
                    No feedbacks found matching your filters.
                  </VuiTypography>
                </VuiBox>
              ) : (
                <Table columns={tableData.columns} rows={tableData.rows} />
              )}
            </VuiBox>
          </Card>

          <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#ffffff !important",
                  borderColor: "#334155",
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#06b6d4 !important",
                  color: "#ffffff !important",
                  borderColor: "#06b6d4",
                  fontWeight: "bold",
                },
              }}
            />
          </Card>
        </VuiBox>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default FeedbacksManagement;
